/* ---------------------------------------------------------------------------
   A structure in isolation: the ground within a radius of it, and the contours
   of that ground, as plain arithmetic.

   Two views share this. The base-map test page (src/basemap/main.ts) masks the
   map outside the circle; the record's 3D model (src/modeler/meshView.ts) lays
   the same circle of ground under the model, in the model's own canvas, with its
   contour lines at their elevations. The radius is one setting for both — chosen
   on either, kept on this device — so the circle on the map and the disc in 3D are
   the same piece of land.

   NO THREE.JS HERE, as in ./terrain.ts: the disc and the isolines are typed
   arrays a BufferGeometry takes as they are, so they can be built and checked
   without a WebGL context.

   THE VERTICAL IS THE TERRAIN MODULE'S: heights relative to the DEM under the
   structure, which is a ~30 m SRTM surface and not a survey. See the note at the
   top of ./terrain.ts. Contour LEVELS are absolute — 120 m, 140 m — because a
   contour is read by its number, and only where each line is drawn is relative.
   --------------------------------------------------------------------------- */

import type { Patch } from './terrain.ts';

/** The radii offered, in kilometres. */
export const ISOLATE_RADII_KM = [1, 2, 5, 10] as const;
export type IsolateRadiusKm = (typeof ISOLATE_RADII_KM)[number];
export const DEFAULT_RADIUS_KM: IsolateRadiusKm = 5;

const STORAGE_KEY = 'bridgewatch.isolateRadiusKm';

/** The radius last chosen on this device, or the default. Never throws. */
export function isolateRadiusKm(): IsolateRadiusKm {
  try {
    const n = Number(localStorage.getItem(STORAGE_KEY));
    return (ISOLATE_RADII_KM as readonly number[]).includes(n) ? (n as IsolateRadiusKm) : DEFAULT_RADIUS_KM;
  } catch {
    return DEFAULT_RADIUS_KM;
  }
}

export function setIsolateRadiusKm(km: IsolateRadiusKm): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(km));
  } catch {
    // Private window or blocked storage: the choice holds for this page only.
  }
}

/* --------------------------- the circle on a map --------------------------- */

/** A circle as a closed ring of [lng, lat], `steps` sides. */
export function circleRing(lat: number, lng: number, radiusM: number, steps = 128): [number, number][] {
  const ring: [number, number][] = [];
  const perLat = 111_320;
  const perLng = 111_320 * Math.cos((lat * Math.PI) / 180);
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    ring.push([lng + (Math.sin(a) * radiusM) / perLng, lat + (Math.cos(a) * radiusM) / perLat]);
  }
  return ring;
}

/**
 * Everything OUTSIDE the circle, as one GeoJSON polygon: the world with the
 * circle as a hole. Filled opaque over a map, it leaves only the circle showing.
 */
/** A GeoJSON polygon feature, spelled out so this module needs no GeoJSON types. */
export interface PolygonFeature {
  type: 'Feature';
  properties: Record<string, never>;
  geometry: { type: 'Polygon'; coordinates: [number, number][][] };
}

export function outsideMask(lat: number, lng: number, radiusM: number): PolygonFeature {
  const world: [number, number][] = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
  ];
  /* The hole wound the other way from the shell, as the GeoJSON spec asks. */
  const hole = circleRing(lat, lng, radiusM).reverse();
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [world, hole] } };
}

/** The circle's bounding box, [[west, south], [east, north]]. */
export function circleBounds(lat: number, lng: number, radiusM: number): [[number, number], [number, number]] {
  const dLat = radiusM / 111_320;
  const dLng = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  return [
    [lng - dLng, lat - dLat],
    [lng + dLng, lat + dLat],
  ];
}

/* --------------------------- the disc in 3D --------------------------- */

/**
 * A square patch whose inscribed circle is the radius, sampled finely enough for
 * the DEM and no finer: about one grid step per 25 m at 5 km, never coarser than
 * 400 per side nor finer than the DEM's ~8 m at z14.
 */
export function isolatePatch(lat: number, lng: number, radiusM: number): Patch {
  const span = radiusM * 2;
  const step = Math.max(8, span / 400);
  const samples = Math.max(64, Math.min(401, Math.round(span / step) + 1));
  return { lat, lng, span, samples };
}

/**
 * The triangles of a terrain grid that lie inside the circle — `indices` as
 * ./terrain.ts builds them, filtered to those whose three corners are within
 * `radius` of the centre. `positions` is that module's x/y/z array.
 */
export function discIndices(positions: Float32Array, indices: Uint32Array, radius: number): Uint32Array {
  const r2 = radius * radius;
  const inside = (i: number): boolean => {
    const x = positions[i * 3]!;
    const z = positions[i * 3 + 2]!;
    return x * x + z * z <= r2;
  };
  const kept: number[] = [];
  for (let k = 0; k + 2 < indices.length; k += 3) {
    const a = indices[k]!;
    const b = indices[k + 1]!;
    const c = indices[k + 2]!;
    if (inside(a) && inside(b) && inside(c)) kept.push(a, b, c);
  }
  return Uint32Array.from(kept);
}

/**
 * Take the DEM's voids and spikes out of a height grid, in place, and say what
 * relief is left.
 *
 * The terrarium tiles carry the odd sample far off the ground around it — a void
 * at −2,492 m two kilometres from a bridge whose ground is at 270 m, in the tiles
 * round PK-N5-1602. Drawn, one of those is a pit kilometres deep that reads from
 * the side as a needle; counted, it stretches the relief so far that the contour
 * interval leaves two lines on the whole disc. So anything further outside the
 * 1st–99th percentile band than half that band's width is clamped to the band,
 * and the band is the relief the contours are spaced over.
 *
 * A BAND DOES NOT CATCH EVERY ONE: a void a few hundred metres down sits inside
 * it on a disc with real relief. So, given the grid's side `n`, a second pass
 * gives any sample further than `local` metres from the median of its 5 × 5
 * neighbourhood that median instead. Real ground passes — a slope is symmetric
 * about its own sample, and the DEM has already smoothed every gorge to tens of
 * metres a step — and a pit or a needle one or two samples wide does not.
 */
export function despike(heights: Float32Array, n = 0, local = 30): { min: number; max: number; clamped: number } {
  if (heights.length === 0) return { min: 0, max: 0, clamped: 0 };
  const sorted = Float32Array.from(heights).sort();
  const lo = sorted[Math.floor(sorted.length * 0.01)]!;
  const hi = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.99))]!;
  const pad = Math.max(5, (hi - lo) * 0.5);
  const floor = lo - pad;
  const ceil = hi + pad;
  let clamped = 0;
  for (let i = 0; i < heights.length; i++) {
    const h = heights[i]!;
    if (h < floor || h > ceil) {
      heights[i] = h < floor ? lo : hi;
      clamped++;
    }
  }

  if (n > 4 && n * n === heights.length) {
    const source = Float32Array.from(heights);
    const window = new Float32Array(24);
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        let k = 0;
        for (let dr = -2; dr <= 2; dr++) {
          const r = row + dr;
          if (r < 0 || r >= n) continue;
          for (let dc = -2; dc <= 2; dc++) {
            const c = col + dc;
            if ((dr === 0 && dc === 0) || c < 0 || c >= n) continue;
            window[k++] = source[r * n + c]!;
          }
        }
        const around = window.subarray(0, k).sort();
        const median = around[k >> 1]!;
        const i = row * n + col;
        if (Math.abs(source[i]! - median) > local) {
          heights[i] = median;
          clamped++;
        }
      }
    }
  }

  let min = Infinity;
  let max = -Infinity;
  for (const h of heights) {
    min = Math.min(min, h);
    max = Math.max(max, h);
  }
  return { min, max, clamped };
}

/** A tidy contour interval for this much relief: about 25 lines top to bottom. */
export function contourInterval(relief: number): number {
  const raw = Math.max(1, relief / 25);
  const mag = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 2.5, 5, 10]) if (raw <= m * mag) return m * mag;
  return 10 * mag;
}

/**
 * Contour lines over a height grid, by marching squares, clipped to a circle.
 *
 * `heights` are the grid's relative heights (row 0 north, as ./terrain.ts lays
 * them out) and `base` the absolute elevation they are relative to, so levels
 * fall on round absolute numbers. Returns line segments as x/y/z pairs in the
 * patch's local frame — X east, Z south, Y up — split into ordinary and index
 * lines (every fifth level), each ready for a LineSegments geometry.
 */
export function contourSegments(
  patch: Patch,
  heights: Float32Array,
  base: number,
  interval: number,
  radius: number,
  lift = 0.6,
): { minor: Float32Array; index: Float32Array } {
  const n = patch.samples;
  const step = patch.span / (n - 1);
  const half = patch.span / 2;
  const r2 = radius * radius;
  const minor: number[] = [];
  const index: number[] = [];

  let lo = Infinity;
  let hi = -Infinity;
  for (const h of heights) {
    lo = Math.min(lo, h + base);
    hi = Math.max(hi, h + base);
  }
  if (!Number.isFinite(lo) || hi - lo < interval) return { minor: new Float32Array(), index: new Float32Array() };

  const first = Math.ceil(lo / interval) * interval;
  for (let level = first; level <= hi; level += interval) {
    const isIndex = Math.round(level / interval) % 5 === 0;
    const out = isIndex ? index : minor;
    const rel = level - base;

    for (let row = 0; row < n - 1; row++) {
      for (let col = 0; col < n - 1; col++) {
        const i = row * n + col;
        /* Corners clockwise from the north-west: a b / d c. */
        const ha = heights[i]!;
        const hb = heights[i + 1]!;
        const hc = heights[i + n + 1]!;
        const hd = heights[i + n]!;
        const code = (ha >= rel ? 8 : 0) | (hb >= rel ? 4 : 0) | (hc >= rel ? 2 : 0) | (hd >= rel ? 1 : 0);
        if (code === 0 || code === 15) continue;

        const x0 = col * step - half;
        const z0 = row * step - half;
        const t = (p: number, q: number) => (p === q ? 0.5 : (rel - p) / (q - p));
        /* Where the level crosses each edge: top (a→b), right (b→c), bottom (d→c), left (a→d). */
        const top = (): [number, number] => [x0 + t(ha, hb) * step, z0];
        const right = (): [number, number] => [x0 + step, z0 + t(hb, hc) * step];
        const bottom = (): [number, number] => [x0 + t(hd, hc) * step, z0 + step];
        const left = (): [number, number] => [x0, z0 + t(ha, hd) * step];

        const pairs: [() => [number, number], () => [number, number]][] = [];
        switch (code) {
          case 1: case 14: pairs.push([left, bottom]); break;
          case 2: case 13: pairs.push([bottom, right]); break;
          case 3: case 12: pairs.push([left, right]); break;
          case 4: case 11: pairs.push([top, right]); break;
          case 6: case 9: pairs.push([top, bottom]); break;
          case 7: case 8: pairs.push([left, top]); break;
          /* Saddles, resolved by the cell's mean. */
          case 5: {
            const mid = (ha + hb + hc + hd) / 4 >= rel;
            if (mid) pairs.push([left, top], [bottom, right]);
            else pairs.push([left, bottom], [top, right]);
            break;
          }
          case 10: {
            const mid = (ha + hb + hc + hd) / 4 >= rel;
            if (mid) pairs.push([left, bottom], [top, right]);
            else pairs.push([left, top], [bottom, right]);
            break;
          }
        }
        for (const [p, q] of pairs) {
          const [px, pz] = p();
          const [qx, qz] = q();
          if (px * px + pz * pz > r2 || qx * qx + qz * qz > r2) continue;
          out.push(px, rel + lift, pz, qx, rel + lift, qz);
        }
      }
    }
  }
  return { minor: Float32Array.from(minor), index: Float32Array.from(index) };
}

/* --------------------------- laying lines on the ground --------------------------- */

/**
 * Height of a grid at a point of its local frame (X east, Z south), bilinear
 * between the four samples round it — the grid as ./terrain.ts lays it out.
 */
export function heightAt(patch: Patch, heights: Float32Array, x: number, z: number): number {
  const n = patch.samples;
  const step = patch.span / (n - 1);
  const fc = Math.min(n - 1.000001, Math.max(0, (x + patch.span / 2) / step));
  const fr = Math.min(n - 1.000001, Math.max(0, (z + patch.span / 2) / step));
  const c = Math.floor(fc);
  const r = Math.floor(fr);
  const tx = fc - c;
  const tz = fr - r;
  const h = (rr: number, cc: number) => heights[rr * n + cc] ?? 0;
  return (
    h(r, c) * (1 - tx) * (1 - tz) + h(r, c + 1) * tx * (1 - tz) + h(r + 1, c) * (1 - tx) * tz + h(r + 1, c + 1) * tx * tz
  );
}

/**
 * A line of [x, z] points laid on the ground: split into pieces no longer than
 * `step` so it follows the surface between samples, lifted `lift` metres so it
 * is not buried in it, and clipped to the circle. Returned as segment pairs of
 * x/y/z, appended to `out`.
 */
export function drapeLine(
  points: readonly [number, number][],
  ground: (x: number, z: number) => number,
  step: number,
  radius: number,
  lift: number,
  out: number[],
): void {
  const r2 = radius * radius;
  for (let k = 1; k < points.length; k++) {
    const [ax, az] = points[k - 1]!;
    const [bx, bz] = points[k]!;
    const pieces = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / step));
    let px = ax;
    let pz = az;
    for (let i = 1; i <= pieces; i++) {
      const qx = ax + ((bx - ax) * i) / pieces;
      const qz = az + ((bz - az) * i) / pieces;
      if (px * px + pz * pz <= r2 && qx * qx + qz * qz <= r2) {
        out.push(px, ground(px, pz) + lift, pz, qx, ground(qx, qz) + lift, qz);
      }
      px = qx;
      pz = qz;
    }
  }
}

/**
 * The ground as a TRIANGLE MESH, edge by edge: the height grid taken every
 * `every`-th sample each way, each square of it split into two triangles along
 * its north-east to south-west diagonal — the same split ./terrain.ts gives the
 * surface — and every edge drawn straight between its two corners, at their
 * heights. So each triangle on screen is one flat facet of a real triangulation
 * of the land, a hundred-odd a side; a mesh at every sample would be a solid
 * sheet at 400. Only triangles wholly inside the circle are drawn.
 */
export function wireframeSegments(patch: Patch, heights: Float32Array, radius: number, every: number, lift = 0.4): Float32Array {
  const n = patch.samples;
  const step = patch.span / (n - 1);
  const half = patch.span / 2;
  const r2 = radius * radius;
  const out: number[] = [];
  const at = (row: number, col: number): [number, number, number] => [
    col * step - half,
    (heights[row * n + col] ?? 0) + lift,
    row * step - half,
  ];
  const inside = (row: number, col: number) => {
    const x = col * step - half;
    const z = row * step - half;
    return x * x + z * z <= r2;
  };
  /* Each edge once, keyed by its two corners' grid indices, however many
     triangles share it. */
  const drawn = new Set<number>();
  const edge = (r1: number, c1: number, r2_: number, c2: number) => {
    const i1 = r1 * n + c1;
    const i2 = r2_ * n + c2;
    const key = Math.min(i1, i2) * n * n + Math.max(i1, i2);
    if (drawn.has(key)) return;
    drawn.add(key);
    out.push(...at(r1, c1), ...at(r2_, c2));
  };
  const last = n - 1 - ((n - 1) % every);
  for (let row = 0; row < last; row += every) {
    for (let col = 0; col < last; col += every) {
      /* a b / c d, split along b–c as the surface is. */
      const r = row + every;
      const c = col + every;
      if (inside(row, col) && inside(row, c) && inside(r, col)) {
        edge(row, col, row, c);
        edge(row, col, r, col);
        edge(row, c, r, col);
      }
      if (inside(row, c) && inside(r, col) && inside(r, c)) {
        edge(row, c, r, col);
        edge(row, c, r, c);
        edge(r, col, r, c);
      }
    }
  }
  return Float32Array.from(out);
}

/* --------------------------- roads and rivers as surfaces --------------------------- */

/** A centre line to be built as a strip: [x, z] points, and whether it is a bridge. */
export interface StripLine {
  points: readonly [number, number][];
  /** Carried on a bridge: run straight from end to end instead of following the ground. */
  bridge: boolean;
  /**
   * Level from edge to edge at the centre line's height, as water is, instead
   * of each edge standing on the ground under it. A river's surface does not
   * climb its banks; where the banks rise above it they hide its edges.
   */
  level?: boolean;
  /**
   * The centre line's height at each point, where it is not the ground's — a
   * river's water surface, which is below its banks. The points are then taken
   * as they are, not resampled.
   */
  heights?: readonly number[];
}

/** A line split into pieces no longer than `step`, keeping every original vertex. */
export function resample(points: readonly [number, number][], step: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let k = 0; k < points.length; k++) {
    const [bx, bz] = points[k]!;
    if (k === 0) {
      pts.push([bx, bz]);
      continue;
    }
    const [ax, az] = points[k - 1]!;
    const pieces = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / step));
    for (let i = 1; i <= pieces; i++) pts.push([ax + ((bx - ax) * i) / pieces, az + ((bz - az) * i) / pieces]);
  }
  return pts;
}

/**
 * Centre lines as flat strips of `width` metres laid on the ground — a road's
 * carriageway or a river's water surface — as positions and triangle indices,
 * with the centre line itself as segment pairs for drawing over it.
 *
 * Each line is first split into pieces no longer than `step`, so the strip bends
 * with the ground between samples. Each edge of the strip stands on the ground
 * under it, so a road on a hillside takes the hill's cross-fall rather than
 * cutting into it; lifted `lift` metres so it is not lost in the surface. A
 * bridge does neither: its height runs straight between the ground at its two
 * ends, as a deck does. Quads with a corner outside the circle are left out.
 */
export function stripGeometry(
  lines: readonly StripLine[],
  ground: (x: number, z: number) => number,
  width: number,
  step: number,
  radius: number,
  lift: number,
): { positions: Float32Array; indices: Uint32Array; centre: Float32Array } {
  const r2 = radius * radius;
  const half = width / 2;
  const positions: number[] = [];
  const indices: number[] = [];
  const centre: number[] = [];

  for (const line of lines) {
    const given = line.heights;
    const pts: [number, number][] = given ? line.points.map(([x, z]) => [x, z]) : resample(line.points, step);
    if (pts.length < 2) continue;

    /* Heights along the centre: the ground's, the line's own, or for a bridge a straight run. */
    const along: number[] = [0];
    for (let i = 1; i < pts.length; i++) along.push(along[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
    const total = along[along.length - 1]! || 1;
    const h0 = ground(pts[0]![0], pts[0]![1]);
    const h1 = ground(pts[pts.length - 1]![0], pts[pts.length - 1]![1]);
    const centreH = (i: number) =>
      given ? given[i]! : line.bridge ? h0 + ((h1 - h0) * along[i]!) / total : ground(pts[i]![0], pts[i]![1]);

    const base = positions.length / 3;
    const inside: boolean[] = [];
    for (let i = 0; i < pts.length; i++) {
      const prev = pts[Math.max(0, i - 1)]!;
      const next = pts[Math.min(pts.length - 1, i + 1)]!;
      let tx = next[0] - prev[0];
      let tz = next[1] - prev[1];
      const len = Math.hypot(tx, tz) || 1;
      tx /= len;
      tz /= len;
      /* Left of the direction of travel. */
      const nx = tz;
      const nz = -tx;
      const [x, z] = pts[i]!;
      const lx = x + nx * half;
      const lz = z + nz * half;
      const rx = x - nx * half;
      const rz = z - nz * half;
      const hc = centreH(i);
      const flat = line.bridge || line.level === true;
      const hl = flat ? hc : ground(lx, lz);
      const hr = flat ? hc : ground(rx, rz);
      positions.push(lx, hl + lift, lz, rx, hr + lift, rz);
      inside.push(lx * lx + lz * lz <= r2 && rx * rx + rz * rz <= r2);
      if (i > 0 && inside[i] && inside[i - 1]) {
        const [px, pz] = pts[i - 1]!;
        centre.push(px, centreH(i - 1) + lift * 1.5, pz, x, hc + lift * 1.5, z);
      }
    }
    for (let i = 1; i < pts.length; i++) {
      if (!inside[i] || !inside[i - 1]) continue;
      const a = base + (i - 1) * 2; // left, previous
      const b = a + 1; //               right, previous
      const c = base + i * 2; //        left, this
      const d = c + 1; //               right, this
      /* Wound to face up whichever way the line runs: left is +normal. */
      indices.push(a, b, c, b, d, c);
    }
  }
  return { positions: Float32Array.from(positions), indices: Uint32Array.from(indices), centre: Float32Array.from(centre) };
}

/* --------------------------- rivers in their channels --------------------------- */

/** A watercourse sunk below its banks: its centre line, its water surface along it, and its width. */
export interface Channel {
  points: [number, number][];
  /** Water surface height at each point. */
  water: number[];
  /** Width of the water, metres. */
  width: number;
}

/**
 * The water surface along a watercourse: `drop` metres below the ground at its
 * centre line, and never rising downstream. OpenStreetMap draws a waterway the
 * way it flows, so the surface is the lowest ground met so far — a DEM that
 * smooths a bank into the channel, or reads a bridge deck, makes no hump in the
 * river. Never more than `maxDig` below the ground, so one low sample upstream
 * does not trench the whole course.
 */
export function channelProfile(
  points: readonly [number, number][],
  ground: (x: number, z: number) => number,
  step: number,
  drop: number,
  maxDig: number,
): { points: [number, number][]; water: number[] } {
  const pts = resample(points, step);
  const water: number[] = [];
  let low = Infinity;
  for (const [x, z] of pts) {
    const g = ground(x, z);
    low = Math.min(low, g);
    water.push(Math.max(low, g - maxDig) - drop);
  }
  return { points: pts, water };
}

/** How steeply a road's embankment rises: metres across per metre up. */
const SIDE_SLOPE = 2;
/** And a channel's banks; and over how many points either side their height is averaged. */
const BANK_SLOPE = 1.5;
const BANK_SMOOTH = 4;
/** How far a bank's top edge runs under the land, so the land always closes over it. */
const BANK_SINK = 0.8;

/**
 * The channels as the ground round them is drawn: their banks as surfaces, from
 * the water's edge, at the water, up and out at 1 in 1.5 until they reach the
 * ground beside them — and their FOOTPRINT, the land each takes up, as polygons
 * of [x, z], for whoever draws the land to leave out.
 *
 * THE LAND IS LEFT OUT, NOT CARVED. The DEM's grid is 25 m a step at a 5 km
 * circle, coarser than a river and its banks, and a grid carved to shape a
 * channel that narrow pokes its triangles through every bank. So the channel
 * is the banks and the water alone, and the land stops at the footprint's edge:
 * `shrink` metres inside the banks' top, so the land overlaps them a little
 * rather than leaving a gap, and the banks' top runs just under the land there.
 *
 * Where one channel's bank would stand in another's water — a tributary at its
 * confluence, or the river it joins — that stretch of bank is left out, so the
 * two waters meet. Quads with a corner outside the circle are left out.
 */
export function bankGeometry(
  channels: readonly Channel[],
  ground: (x: number, z: number) => number,
  radius: number,
  shrink: number,
): { positions: Float32Array; indices: Uint32Array; footprint: [number, number][][] } {
  const r2 = radius * radius;
  const positions: number[] = [];
  const indices: number[] = [];
  const footprint: [number, number][][] = [];
  const water = wetTest(channels, [], -0.5);
  channels.forEach((ch, ci) => {
    const half = ch.width / 2;
    const base = positions.length / 3;
    const count = ch.points.length;
    /* The way across at each point, and how high each bank stands above the water there. */
    const across: [number, number][] = [];
    const rise = [new Float64Array(count), new Float64Array(count)];
    for (let i = 0; i < count; i++) {
      const prev = ch.points[Math.max(0, i - 1)]!;
      const next = ch.points[Math.min(count - 1, i + 1)]!;
      const tx = next[0] - prev[0];
      const tz = next[1] - prev[1];
      const len = Math.hypot(tx, tz) || 1;
      across.push([tz / len, -tx / len]);
      const [x, z] = ch.points[i]!;
      for (const [s, sign] of [[0, 1], [1, -1]] as const) {
        rise[s]![i] = Math.max(0.3, ground(x + (sign * tz * half) / len, z - (sign * tx * half) / len) - ch.water[i]!);
      }
    }
    /* Averaged along the course, or the DEM's noise makes every bank top ragged. */
    const smooth = rise.map((r) =>
      r.map((_, i) => {
        let sum = 0;
        let k = 0;
        for (let j = Math.max(0, i - BANK_SMOOTH); j <= Math.min(count - 1, i + BANK_SMOOTH); j++, k++) sum += r[j]!;
        return sum / k;
      }),
    );
    /* Per point and side: the water's edge, the bank's top, and the footprint's edge. */
    const edge: [number, number][][] = [[], []];
    const top: [number, number][][] = [[], []];
    const rim: [number, number][][] = [[], []];
    const dry: boolean[][] = [[], []];
    const inside: boolean[] = [];
    for (let i = 0; i < count; i++) {
      const [x, z] = ch.points[i]!;
      const w = ch.water[i]!;
      let ok = true;
      /* Per point: left edge, left top, right edge, right top. */
      for (const [s, sign] of [[0, 1], [1, -1]] as const) {
        const nx = across[i]![0] * sign;
        const nz = across[i]![1] * sign;
        const off = half + BANK_SLOPE * smooth[s]![i]!;
        const e: [number, number] = [x + nx * half, z + nz * half];
        const t: [number, number] = [x + nx * off, z + nz * off];
        const inner = Math.max(half, off - shrink);
        edge[s]!.push(e);
        top[s]!.push(t);
        rim[s]!.push([x + nx * inner, z + nz * inner]);
        dry[s]!.push(!water.at(e[0], e[1], ci) && !water.at(t[0], t[1], ci));
        positions.push(e[0], w, e[1], t[0], Math.max(ground(t[0], t[1]) - BANK_SINK, w + 0.2), t[1]);
        ok &&= t[0] * t[0] + t[1] * t[1] <= r2;
      }
      inside.push(ok);
    }
    for (let i = 1; i < count; i++) {
      if (!inside[i] || !inside[i - 1]) continue;
      const p = base + (i - 1) * 4;
      const q = base + i * 4;
      const [a, b] = [ch.points[i - 1]!, ch.points[i]!];
      for (const s of [0, 1] as const) {
        footprint.push([a, b, edge[s]![i]!, edge[s]![i - 1]!]);
        if (!dry[s]![i] || !dry[s]![i - 1]) continue;
        footprint.push([edge[s]![i - 1]!, edge[s]![i]!, rim[s]![i]!, rim[s]![i - 1]!]);
        if (s === 0) indices.push(p + 1, p, q + 1, p, q, q + 1); // left bank
        else indices.push(p + 2, p + 3, q + 2, p + 3, q + 3, q + 2); // right bank
      }
    }
  });
  return { positions: Float32Array.from(positions), indices: Uint32Array.from(indices), footprint };
}

/** Where water is, for a road to stop short of. */
export interface Wet {
  /** On the water or within `margin` of it — leaving out the course numbered `skip`. */
  at(x: number, z: number, skip?: number): boolean;
  /** Possibly within `r` of it — cheap, and allowed to say yes too often. */
  near(x: number, z: number, r: number): boolean;
}

/**
 * The water a road may not cross: within half its width (plus `margin`) of a
 * watercourse's centre line, or inside a mapped water area — `areas` are each a
 * feature's rings of [x, z], tested even-odd, so an island is dry.
 */
export function wetTest(
  courses: readonly { points: readonly [number, number][]; width: number }[],
  areas: readonly (readonly (readonly [number, number][])[])[],
  margin: number,
): Wet {
  const CELL = 50;
  const key = (ix: number, iz: number) => (ix + 2000) * 4096 + (iz + 2000);
  const segs: { ax: number; az: number; bx: number; bz: number; r: number; course: number }[] = [];
  const buckets = new Map<number, number[]>();
  courses.forEach((c, course) => {
    const r = c.width / 2 + margin;
    for (let k = 1; k < c.points.length; k++) {
      const [ax, az] = c.points[k - 1]!;
      const [bx, bz] = c.points[k]!;
      const id = segs.push({ ax, az, bx, bz, r, course }) - 1;
      for (let ix = Math.floor((Math.min(ax, bx) - r) / CELL); ix <= Math.floor((Math.max(ax, bx) + r) / CELL); ix++) {
        for (let iz = Math.floor((Math.min(az, bz) - r) / CELL); iz <= Math.floor((Math.max(az, bz) + r) / CELL); iz++) {
          const list = buckets.get(key(ix, iz));
          if (list) list.push(id);
          else buckets.set(key(ix, iz), [id]);
        }
      }
    }
  });
  const boxes = areas.map((rings) => {
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (const ring of rings) for (const [x, z] of ring) {
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
    }
    return { x0, z0, x1, z1, rings };
  });
  return {
    at(x, z, skip = -1) {
      for (const id of buckets.get(key(Math.floor(x / CELL), Math.floor(z / CELL))) ?? []) {
        const s = segs[id]!;
        if (s.course === skip) continue;
        const vx = s.bx - s.ax;
        const vz = s.bz - s.az;
        const t = Math.max(0, Math.min(1, ((x - s.ax) * vx + (z - s.az) * vz) / (vx * vx + vz * vz || 1)));
        if (Math.hypot(x - (s.ax + vx * t), z - (s.az + vz * t)) <= s.r) return true;
      }
      for (const b of boxes) {
        if (x < b.x0 - margin || x > b.x1 + margin || z < b.z0 - margin || z > b.z1 + margin) continue;
        let inside = false;
        for (const ring of b.rings) {
          for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [xi, zi] = ring[i]!;
            const [xj, zj] = ring[j]!;
            if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
            /* Within the margin of the shore counts too. */
            const vx = xi - xj;
            const vz = zi - zj;
            const t = Math.max(0, Math.min(1, ((x - xj) * vx + (z - zj) * vz) / (vx * vx + vz * vz || 1)));
            if (Math.hypot(x - (xj + vx * t), z - (zj + vz * t)) <= margin) return true;
          }
        }
        if (inside) return true;
      }
      return false;
    },
    near(x, z, r) {
      for (let ix = Math.floor((x - r) / CELL); ix <= Math.floor((x + r) / CELL); ix++) {
        for (let iz = Math.floor((z - r) / CELL); iz <= Math.floor((z + r) / CELL); iz++) {
          if (buckets.has(key(ix, iz))) return true;
        }
      }
      return boxes.some((b) => x >= b.x0 - r && x <= b.x1 + r && z >= b.z0 - r && z <= b.z1 + r);
    },
  };
}

/**
 * The footprints as a mask over the patch: one byte a texel, `size` a side,
 * 255 where a channel is and 0 where the land is — row 0 north, column 0 west,
 * as the terrain grid. Rasterised by whatever 2D context the caller has.
 */
export function footprintMask(
  footprint: readonly (readonly [number, number][])[],
  span: number,
  size: number,
  ctx: { fillStyle: string | CanvasGradient | CanvasPattern; beginPath(): void; moveTo(x: number, y: number): void; lineTo(x: number, y: number): void; closePath(): void; fill(): void; fillRect(x: number, y: number, w: number, h: number): void; getImageData(x: number, y: number, w: number, h: number): { data: Uint8ClampedArray } },
): Uint8Array {
  const half = span / 2;
  const px = (v: number) => ((v + half) / span) * size;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#fff';
  for (const poly of footprint) {
    ctx.beginPath();
    poly.forEach(([x, z], i) => (i ? ctx.lineTo(px(x), px(z)) : ctx.moveTo(px(x), px(z))));
    ctx.closePath();
    ctx.fill();
  }
  const rgba = ctx.getImageData(0, 0, size, size).data;
  const out = new Uint8Array(size * size);
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4]!;
  return out;
}

/* --------------------------- roads on their embankments --------------------------- */

/** A road to be built up: its centre line, whether it is a bridge, and its fill height. */
export interface RoadLine {
  points: readonly [number, number][];
  bridge: boolean;
  /** Crown above the ground under its centre line, metres. */
  height: number;
}

/** How finely a road is followed where it meets water, so it stops at the bank. */
const WET_STEP = 2;

/**
 * Roads as built: each carriageway level from edge to edge, `height` metres
 * above the ground under its centre line (`crown`), on an embankment whose
 * sides fall at 1 in 2 to the ground beside it (`toe`) and run a little into
 * it, so no gap shows between fill and land.
 *
 * NO ROAD CROSSES WATER. Where `wet` says a point of the road is on water, the
 * road is cut there, and each cut end closed with a face down to the ground, as
 * an abutment would be — the structure, or nothing, spans the gap. A stretch
 * OpenStreetMap marks as a bridge is not cut: it runs straight between the
 * crowns at its two ends, as a deck does, with no fill under it.
 *
 * Returned as the carriageway, the fill, and the centre line as segment pairs.
 * Quads with a corner outside the circle are left out.
 */
export function embankedRoads(
  lines: readonly RoadLine[],
  crown: (x: number, z: number) => number,
  toe: (x: number, z: number) => number,
  wet: Wet | null,
  width: number,
  step: number,
  radius: number,
): {
  deck: { positions: Float32Array; indices: Uint32Array };
  fill: { positions: Float32Array; indices: Uint32Array };
  centre: Float32Array;
} {
  const r2 = radius * radius;
  const half = width / 2;
  const SINK = 0.5;
  const deckPos: number[] = [];
  const deckIdx: number[] = [];
  const fillPos: number[] = [];
  const fillIdx: number[] = [];
  const centre: number[] = [];
  const inCircle = (x: number, z: number) => x * x + z * z <= r2;

  /* A run of dry road, as one strip of carriageway and its two side slopes. */
  const build = (pts: [number, number][], heights: number[], bridge: boolean, capStart: boolean, capEnd: boolean) => {
    const dBase = deckPos.length / 3;
    const fBase = fillPos.length / 3;
    const inside: boolean[] = [];
    const ends: { cl: number[]; cr: number[] }[] = [];
    for (let i = 0; i < pts.length; i++) {
      const prev = pts[Math.max(0, i - 1)]!;
      const next = pts[Math.min(pts.length - 1, i + 1)]!;
      let tx = next[0] - prev[0];
      let tz = next[1] - prev[1];
      const len = Math.hypot(tx, tz) || 1;
      tx /= len;
      tz /= len;
      const nx = tz;
      const nz = -tx;
      const [x, z] = pts[i]!;
      const h = heights[i]!;
      const lx = x + nx * half, lz = z + nz * half;
      const rx = x - nx * half, rz = z - nz * half;
      deckPos.push(lx, h, lz, rx, h, rz);
      /* Each side's toe: as far out as the fill must reach to come down (or up) to the ground. */
      const side = (sx: number, sz: number, sign: number): number[] => {
        const off = half + SIDE_SLOPE * Math.max(0.3, Math.abs(h - toe(sx, sz)));
        const px = x + nx * off * sign;
        const pz = z + nz * off * sign;
        return [px, toe(px, pz) - SINK, pz];
      };
      const tl = side(lx, lz, 1);
      const tr = side(rx, rz, -1);
      fillPos.push(lx, h, lz, rx, h, rz, ...tl, ...tr);
      ends.push({ cl: [lx, h, lz], cr: [rx, h, rz] });
      inside.push(inCircle(tl[0]!, tl[2]!) && inCircle(tr[0]!, tr[2]!) && inCircle(lx, lz) && inCircle(rx, rz));
      if (i > 0 && inside[i] && inside[i - 1]) {
        const [px, pz] = pts[i - 1]!;
        centre.push(px, heights[i - 1]! + 0.05, pz, x, h + 0.05, z);
      }
    }
    for (let i = 1; i < pts.length; i++) {
      if (!inside[i] || !inside[i - 1]) continue;
      const a = dBase + (i - 1) * 2;
      const c = dBase + i * 2;
      deckIdx.push(a, a + 1, c, a + 1, c + 1, c);
      if (bridge) continue;
      /* Fill vertices per point: crown left, crown right, toe left, toe right. */
      const p = fBase + (i - 1) * 4;
      const q = fBase + i * 4;
      fillIdx.push(p + 2, p, q + 2, p, q, q + 2); // left slope
      fillIdx.push(p + 1, p + 3, q + 1, p + 3, q + 3, q + 1); // right slope
    }
    if (bridge) return;
    /* A cut end, closed down to the ground: under the carriageway and under both slopes. */
    const cap = (i: number) => {
      if (!inside[i]) return;
      const { cl, cr } = ends[i]!;
      const gl = [cl[0]!, toe(cl[0]!, cl[2]!) - SINK, cl[2]!];
      const gr = [cr[0]!, toe(cr[0]!, cr[2]!) - SINK, cr[2]!];
      const v = fillPos.length / 3;
      fillPos.push(...cl, ...cr, ...gl, ...gr);
      const f = fBase + i * 4;
      fillIdx.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
      fillIdx.push(f, f + 2, v + 2, f + 1, v + 3, f + 3);
    };
    if (capStart) cap(0);
    if (capEnd) cap(pts.length - 1);
  };

  for (const line of lines) {
    /* Finely where water is near, so the cut falls at the bank; at the grid's step elsewhere. */
    const pts: [number, number][] = [];
    for (let k = 0; k < line.points.length; k++) {
      const [bx, bz] = line.points[k]!;
      if (k === 0) {
        pts.push([bx, bz]);
        continue;
      }
      const [ax, az] = line.points[k - 1]!;
      const pieces = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / step));
      for (let i = 1; i <= pieces; i++) {
        const sx = ax + ((bx - ax) * (i - 1)) / pieces;
        const sz = az + ((bz - az) * (i - 1)) / pieces;
        const ex = ax + ((bx - ax) * i) / pieces;
        const ez = az + ((bz - az) * i) / pieces;
        const len = Math.hypot(ex - sx, ez - sz);
        const fine = !line.bridge && wet !== null && wet.near((sx + ex) / 2, (sz + ez) / 2, len / 2 + 30);
        const sub = fine ? Math.max(1, Math.ceil(len / WET_STEP)) : 1;
        for (let j = 1; j <= sub; j++) pts.push([sx + ((ex - sx) * j) / sub, sz + ((ez - sz) * j) / sub]);
      }
    }
    if (pts.length < 2) continue;

    if (line.bridge) {
      const h0 = crown(pts[0]![0], pts[0]![1]) + line.height;
      const h1 = crown(pts[pts.length - 1]![0], pts[pts.length - 1]![1]) + line.height;
      const along: number[] = [0];
      for (let i = 1; i < pts.length; i++) along.push(along[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
      const total = along[along.length - 1]! || 1;
      build(pts, along.map((s) => h0 + ((h1 - h0) * s) / total), true, false, false);
      continue;
    }

    const dry = pts.map(([x, z]) => !(wet?.at(x, z) ?? false));
    let start = -1;
    for (let i = 0; i <= pts.length; i++) {
      if (i < pts.length && dry[i]) {
        if (start < 0) start = i;
        continue;
      }
      if (start >= 0 && i - start >= 2) {
        const run = pts.slice(start, i);
        build(run, run.map(([x, z]) => crown(x, z) + line.height), false, start > 0, i < pts.length);
      }
      start = -1;
    }
  }
  return {
    deck: { positions: Float32Array.from(deckPos), indices: Uint32Array.from(deckIdx) },
    fill: { positions: Float32Array.from(fillPos), indices: Uint32Array.from(fillIdx) },
    centre: Float32Array.from(centre),
  };
}

/* --------------------------- standing a structure on its road --------------------------- */

/** Where a structure's road passes, in the local frame, and which way it runs. */
export interface RoadFix {
  x: number;
  z: number;
  /** Unit direction of the road there, in x/z. */
  dx: number;
  dz: number;
  /** How far the pin is from it, metres. */
  distance: number;
  /** Whether it is a stretch OpenStreetMap marks as a bridge. */
  bridge: boolean;
}

/**
 * The road a structure carries: the nearest point of the given lines to the
 * origin (the pin), preferring a stretch marked as a bridge within
 * \`bridgeReach\` metres over any road within \`roadReach\` — the mapped bridge IS
 * the structure, where somebody has mapped it, and the nearest road merely
 * probably is. Null when nothing is close enough to believe.
 */
export function roadAt(
  lines: readonly { points: readonly [number, number][]; bridge: boolean }[],
  bridgeReach = 120,
  roadReach = 60,
): RoadFix | null {
  let best: RoadFix | null = null;
  const better = (cand: RoadFix): boolean => {
    if (!best) return true;
    if (cand.bridge !== best.bridge) return cand.bridge;
    return cand.distance < best.distance;
  };
  for (const line of lines) {
    for (let k = 1; k < line.points.length; k++) {
      const [ax, az] = line.points[k - 1]!;
      const [bx, bz] = line.points[k]!;
      const vx = bx - ax;
      const vz = bz - az;
      const len2 = vx * vx + vz * vz;
      if (len2 === 0) continue;
      const t = Math.max(0, Math.min(1, -(ax * vx + az * vz) / len2));
      const x = ax + vx * t;
      const z = az + vz * t;
      const distance = Math.hypot(x, z);
      if (distance > (line.bridge ? bridgeReach : roadReach)) continue;
      const len = Math.sqrt(len2);
      const cand: RoadFix = { x, z, dx: vx / len, dz: vz / len, distance, bridge: line.bridge };
      if (better(cand)) best = cand;
    }
  }
  return best;
}

/** Units a model might have been exported in, as metres per unit. */
const UNITS = [
  { name: 'metres', perUnit: 1 },
  { name: 'feet', perUnit: 0.3048 },
  { name: 'inches', perUnit: 0.0254 },
  { name: 'centimetres', perUnit: 0.01 },
  { name: 'millimetres', perUnit: 0.001 },
] as const;

/**
 * The unit a model's length was most likely drawn in, given the length on
 * record: the one that brings the two closest, and metres unless another is
 * plainly nearer — within 25% where metres is out by more than a factor of 2.
 * A model and a record that disagree by less than that are both in metres and
 * simply disagree, which is worth saying but not worth rescaling for.
 */
export function modelUnit(modelLength: number, recordedLength: number | null): { name: string; perUnit: number } {
  if (!recordedLength || !(modelLength > 0)) return UNITS[0];
  const off = (u: { perUnit: number }) => Math.abs(Math.log((modelLength * u.perUnit) / recordedLength));
  if (off(UNITS[0]) <= Math.log(2)) return UNITS[0];
  const best = [...UNITS].sort((a, b) => off(a) - off(b))[0]!;
  return off(best) <= Math.log(1.25) ? best : UNITS[0];
}
